import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { es } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';

import CloseIcon from '@material-ui/icons/Close';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { Box, Button, Drawer, IconButton, Typography } from '@material-ui/core';

import { DRAWERS } from 'utils/constants';
import { useDrawer } from 'hooks/drawer.hook';
import { setModalStatus } from 'redux/slices/modal.slice';

import { nutritionistAppointmentSelector } from 'features/appointments/selectors/nutritionist-appointments.selector';
import { useRejectNutritionistAppointmentService } from 'features/appointments/services/reject-nutritionist-appointment.service';
import { useAcceptNutritionistAppointmentService } from 'features/appointments/services/accept-nutritionist-appointment.service';

import { useStyles } from './nutritionist-appointment-drawer.styles';

export const NutritionistAppointmentDrawer = () => {
  const [isRescheduleWarningVisible, setIsRescheduleWarningVisible] =
    useState(false);
  const [canReschedule, setCanReschedule] = useState(true);
  const [isRescheduleEnabled, setIsRescheduleEnabled] = useState(false);

  const appointment = useSelector(nutritionistAppointmentSelector);

  const { visibility, handleClose } = useDrawer(
    DRAWERS.nutritionistAppointment,
  );

  const acceptAppointmentService = useAcceptNutritionistAppointmentService();

  const rejectAppointmentService = useRejectNutritionistAppointmentService();

  const { push } = useHistory();

  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const onRescheduleAppointmentHandler = () => {
    if (!isRescheduleEnabled) {
      setIsRescheduleWarningVisible(true);
      return;
    }

    setTimeout(() => {
      const {
        _id: appointmentId,
        nutritionist: { _id: nutritionistId },
      } = appointment || {};

      const url = `/dashboard/main/nutritionist-appointment?mode=reschedule&appointmentId=${appointmentId}&nutritionistId=${nutritionistId}`;
      push(url);
    }, 200);

    setTimeout(() => {
      handleClose();
    }, 700);
  };

  const onCancelAppointmentHandler = () => {
    if (!isRescheduleEnabled) {
      setIsRescheduleWarningVisible(true);
      return;
    }

    dispatch(
      setModalStatus({
        name: 'CANCEL_APPOINTMENT_WITH_NUTRITIONIST_WARNING',
        isActive: true,
      }),
    );
  };

  const onCopyToClipboardHandler = () => {
    const shouldUseAddress = ['onsite', 'home'].includes(appointment?.mode);
    const { street, hall, country, zipCode, mapUrl } =
      appointment.office.address;

    const address = shouldUseAddress
      ? `${street}, ${hall}, ${country}, ${zipCode}`
      : mapUrl;

    navigator.clipboard.writeText(address).then(() => {
      enqueueSnackbar('Dirección copiada con éxito', {
        variant: 'success',
      });
    });
  };

  const onAcceptAppointmentHandler = () => {
    acceptAppointmentService.mutate(appointment?._id ?? '');
  };

  const onRejectAppointmentHandler = () => {
    rejectAppointmentService.mutate(appointment?._id ?? '');
  };

  const isPendingToConfirm =
    appointment?.status === 'pending' &&
    appointment?.nextActionRole === 'client';

  const nutritionistName = `${appointment?.nutritionist?.name ?? ''} ${
    appointment?.nutritionist?.lastName ?? ''
  }`;

  const classes = useStyles();

  useEffect(() => {
    if (!isEmpty(appointment)) {
      const { canReschedule } =
        appointment.nutritionist.nutritionistProfile.settings;
      const { canRescheduleAndCancel } = appointment;
      setIsRescheduleEnabled(canRescheduleAndCancel);
      setCanReschedule(canReschedule);
    }
  }, [appointment]);

  if (!appointment) {
    return null;
  }

  return (
    <Drawer
      anchor="right"
      open={visibility}
      onClose={handleClose}
      classes={{ paper: classes.paper }}
    >
      <Box component="header">
        <IconButton onClick={handleClose}>
          <CloseIcon color="primary" />
        </IconButton>
        <Box>
          <Typography
            variant="h5"
            className={`${classes.title} ${classes.mainTitle}`}
          >
            Detalles de tu consulta
          </Typography>
          <Typography variant="h6" align="center">
            Nutriólogo: {nutritionistName}
          </Typography>

          {isPendingToConfirm ? (
            <Box mt={2} component="section">
              <Button
                fullWidth
                disabled={acceptAppointmentService.isLoading}
                variant="contained"
                onClick={onAcceptAppointmentHandler}
              >
                Confirmar cita
              </Button>
              <br />
              <br />
              <Button
                fullWidth
                disabled={rejectAppointmentService.isLoading}
                color="primary"
                variant="outlined"
                onClick={onRejectAppointmentHandler}
              >
                Rechazar cita
              </Button>
            </Box>
          ) : (
            <Box mt={2} component="section">
              {!appointment?.nutritionist?.nutritionistProfile?.canUseAgenda ? (
                <Box mb={1}>
                  <Typography variant="h6">
                    No es posible reagendar la consulta, consulta con tu
                    nutriólogo.
                  </Typography>
                  <br />
                  <Typography variant="h6">
                    No es posible cancelar la consulta, consulta con tu
                    nutriólogo.
                  </Typography>
                </Box>
              ) : canReschedule ? (
                <>
                  {isRescheduleWarningVisible && (
                    <Typography
                      variant="h6"
                      align="center"
                      style={{ marginBottom: '16px' }}
                    >
                      ¡Uh oh! Estás fuera del margen de tiempo requerido para
                      poder modificar tu consulta. Por favor, ponte directamente
                      en contacto con tu nutriólogo (a) para consultar sus
                      políticas de reprogramación / cancelación de consultas.
                    </Typography>
                  )}
                  <Box mb={1}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={onRescheduleAppointmentHandler}
                    >
                      Reagendar
                    </Button>
                  </Box>
                  <Box>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="primary"
                      onClick={onCancelAppointmentHandler}
                    >
                      Cancelar
                    </Button>
                  </Box>
                </>
              ) : (
                <Typography variant="h6" align="center">
                  Lo sentimos, no es posible reagendar ni cancelar. Si deseas
                  modificar tu consulta, por favor ponte directamente en
                  contacto con tu nutriólogo(a).
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Box pt={4} component="main">
        <Typography variant="h5" align="center">
          ¿Cuándo?
        </Typography>
        <Box my={2} className={classes.dashed}>
          <Typography variant="body2">
            {appointment?.startDate &&
              format(
                new Date(appointment.startDate),
                "EEEE' 'd' de 'LLLL' del 'y'",
                { locale: es },
              )}
          </Typography>
          <Typography variant="body2">
            {appointment?.startDate &&
              formatInTimeZone(
                new Date(appointment.startDate),
                appointment.timeZone,
                "'A las 'h:mm aaaa",
                {
                  locale: es,
                },
              )}
          </Typography>
          {['onsite', 'home'].includes(appointment.mode) &&
            Intl.DateTimeFormat().resolvedOptions().timeZone !==
              appointment.timeZone && (
              <Typography variant="body2">
                Recuerda, te encuentras en una zona horaria diferente a la de tu
                consulta.
              </Typography>
            )}
        </Box>
      </Box>

      <Box pt={4} component="section">
        <Typography variant="h5" align="center">
          ¿Dónde?
        </Typography>
        <Box mt={2} className={classes.dashed}>
          <Box>
            {['onsite', 'home'].includes(appointment?.mode) ? (
              <>
                <Typography variant="body2">
                  {appointment?.office?.address?.street ?? ''},{' '}
                  {appointment?.office?.address?.hall ?? ''},{' '}
                  {appointment?.office?.address?.country ?? ''},{' '}
                  {appointment?.office?.address?.zipCode ?? ''}
                </Typography>
                <Typography variant="body2">
                  {appointment?.office?.address?.mapUrl}
                </Typography>
              </>
            ) : (
              <Typography variant="body2">
                {appointment?.office?.address?.mapUrl}
              </Typography>
            )}
            <Box mt={1}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<FileCopyIcon />}
                onClick={onCopyToClipboardHandler}
              >
                Copiar dirección
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {['home', 'online'].includes(appointment.mode) && (
        <>
          <Box pt={4} component="section">
            <Typography variant="h5" align="center">
              Contacto
            </Typography>
            <Box mt={2} className={classes.dashed}>
              <Typography variant="body2">
                {appointment?.office?.phoneNumber}
              </Typography>
            </Box>
          </Box>
        </>
      )}

      <Box pt={4} component="section">
        <Typography variant="h5" align="center">
          Comentarios del nutriólogo
        </Typography>
        <Box mt={2} className={classes.dashed}>
          <Typography variant="body2">
            {appointment.patientComment || 'No hay comentarios'}
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};
